import dayjs from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import Accessibility from "../categories/Accessibility";
import Performance from "../categories/Performance";
import Audits from "../components/Audits";
import CategorySummary from "../components/CategorySummary";
import History from "../components/HistoricalData";
import MainLoader from "../components/MainLoader";
import { clearUrl, setError } from "../features/web/web-slice";
import { fetchPageSpeed } from "../services/pageSpeedService";
import { createSummary, fetchSummaries } from "../services/summaryService";
import type { PageSpeedResponse, SummaryRecord } from "../types/analyzer";

const buildSummary = (
	data: PageSpeedResponse,
	uid: string
): SummaryRecord => {
	const performanceCategory = data.lighthouseResult.categories.performance;
	const weightedAuditRefs = performanceCategory.auditRefs.filter(
		(auditRef) => auditRef.weight > 0
	);
	const audits = weightedAuditRefs.map(
		(auditRef) => data.lighthouseResult.audits[auditRef.id]
	);
	const weightedScore = audits.reduce((acc, audit, index) => {
		const auditScore = audit?.score ?? 1;
		return acc + auditScore * weightedAuditRefs[index].weight;
	}, 0);

	return {
		uid,
		scores: {
			performance: parseFloat((weightedScore / 100).toFixed(2)),
			accessibility:
				data.lighthouseResult.categories.accessibility.score ?? 0,
			bestPractices:
				data.lighthouseResult.categories["best-practices"].score ?? 0,
			seo: data.lighthouseResult.categories.seo.score ?? 0,
			pwa: data.lighthouseResult.categories.pwa.score ?? 0,
		},
		timestamp: Date.now(),
		web: data.loadingExperience.initial_url,
	};
};

function Results() {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();
	const url = useAppSelector((state) => state.web.url);
	const error = useAppSelector((state) => state.web.error);
	const user = useAppSelector((state) => state.login);
	const lastPersistedRunRef = useRef<string | null>(null);

	const pageSpeedQuery = useQuery({
		queryKey: ["page-speed", url],
		queryFn: () => {
			if (!url) {
				throw new Error("Missing target url");
			}
			return fetchPageSpeed(url);
		},
		enabled: Boolean(url),
		refetchOnMount: false,
	});

	const historyQuery = useQuery({
		queryKey: ["summaries", user.uid],
		queryFn: () => fetchSummaries(user.uid),
		enabled: Boolean(user.uid),
	});

	const { mutateAsync: persistSummary } = useMutation({
		mutationFn: createSummary,
		onSuccess: () => {
			if (user.uid) {
				queryClient.invalidateQueries({
					queryKey: ["summaries", user.uid],
				});
			}
		},
	});

	useEffect(() => {
		if (pageSpeedQuery.isError && pageSpeedQuery.error instanceof Error) {
			dispatch(setError(pageSpeedQuery.error.message));
		} else if (pageSpeedQuery.isSuccess) {
			dispatch(setError(null));
		}
	}, [dispatch, pageSpeedQuery.error, pageSpeedQuery.isError, pageSpeedQuery.isSuccess]);

	useEffect(() => {
		if (!url) {
			lastPersistedRunRef.current = null;
		}
	}, [url]);

	useEffect(() => {
		if (!pageSpeedQuery.data || !url || !user.uid) {
			return;
		}
		const runId =
			pageSpeedQuery.data.lighthouseResult.fetchTime ?? `${url}-${Date.now()}`;

		if (lastPersistedRunRef.current === runId) {
			return;
		}
		const summary = buildSummary(pageSpeedQuery.data, user.uid);
		void persistSummary(summary);
		lastPersistedRunRef.current = runId;
	}, [pageSpeedQuery.data, persistSummary, url, user.uid]);

	const currentSummary = useMemo(() => {
		if (!pageSpeedQuery.data) {
			return null;
		}
		return buildSummary(pageSpeedQuery.data, user.uid || "local-preview");
	}, [pageSpeedQuery.data, user.uid]);

	const handleReset = () => {
		dispatch(clearUrl());
	};

	if (!url) {
		return (
			<section className="page page--results page--placeholder">
				<p>Ingresa un sitio para generar un nuevo reporte.</p>
				<button className="button button--primary" onClick={handleReset}>
					Volver al analizador
				</button>
			</section>
		);
	}

	if (pageSpeedQuery.isLoading || historyQuery.isLoading) {
		return <MainLoader />;
	}

	if (pageSpeedQuery.isError || error) {
		const errorMessage =
			(pageSpeedQuery.error instanceof Error && pageSpeedQuery.error.message) ||
			error ||
			"Unable to run the audit. Please try again.";
		return (
			<Navigate
				to="/error"
				replace
				state={{
					message: errorMessage,
				}}
			/>
		);
	}

	const history = historyQuery.data ?? [];
	const categoriesSummary = history[0] ?? currentSummary;
	const lighthouseResult = pageSpeedQuery.data?.lighthouseResult;
	const historyError =
		historyQuery.isError && historyQuery.error instanceof Error
			? historyQuery.error.message
			: null;

	if (!lighthouseResult || !categoriesSummary) {
		return (
			<section className="page page--results page--placeholder">
				<p>
					No pudimos construir el reporte con los datos recibidos. Intenta nuevamente
					en unos segundos.
				</p>
				<div className="hero__actions">
					<button className="button button--primary" onClick={handleReset}>
						Volver al analizador
					</button>
				</div>
			</section>
		);
	}

	const sourceUrl =
		pageSpeedQuery.data?.loadingExperience.initial_url || `https://${url}`;
	const fetchedAt =
		lighthouseResult.fetchTime ??
		new Date().toISOString();

	return (
		<section className="page page--results">
			<header className="results__header">
				<div>
					<p className="eyebrow">Resultados para</p>
					<h1>{sourceUrl.replace(/^https?:\/\//, "")}</h1>
					<p>Última ejecución: {dayjs(fetchedAt).format("DD MMM YYYY HH:mm")}</p>
				</div>
				<div className="hero__actions">
					<button className="button button--ghost" onClick={handleReset}>
						Nueva auditoría
					</button>
				</div>
			</header>

			<div className="results__summary-grid">
				<div className="panel">
					<header className="panel__header panel__header--compact">
						<p className="eyebrow">Resumen ejecutivo</p>
						<h2>Áreas priorizadas</h2>
					</header>
					<CategorySummary categories={categoriesSummary} />
				</div>

				<div className="panel results__history-panel">
					<header className="panel__header panel__header--compact">
						<p className="eyebrow">Historial</p>
						<h2>Ejecuciones anteriores</h2>
					</header>
					{historyError && (
						<p className="form-error">No se pudo cargar el historial: {historyError}</p>
					)}
					<History history={history} />
				</div>
			</div>

			<div className="results__sections">
				<div className="panel">
					<Performance
						performanceCategory={lighthouseResult.categories.performance}
						audits={lighthouseResult.audits}
					/>
				</div>
				<div className="panel">
					<Accessibility
						accessibilityCategory={lighthouseResult.categories.accessibility}
						audits={lighthouseResult.audits}
					/>
				</div>
			</div>

			<div className="panel">
				<header className="panel__header panel__header--compact">
					<p className="eyebrow">Detalle completo</p>
					<h2>Auditorías Lighthouse</h2>
				</header>
				<Audits audits={lighthouseResult.audits} />
			</div>
		</section>
	);
}

export default Results;
